import React, { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, useReducedMotion } from 'framer-motion';
import { Logo } from './Logo';
import { SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, UserIcon, BriefcaseIcon, ShoppingBagIcon, LifeBuoyIcon, LockIcon, PackageIcon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
const SPRING = {
  stiffness: 300,
  damping: 32,
  mass: 0.6
};
export function Sidebar() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth
  } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const navItems: NavItem[] = [{
    to: '/',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/orders',
    label: 'My Orders',
    icon: PackageIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  },
  // Business Hub is always available to signed-in users; My Shop appears
  // once the hub has been activated.
  {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  } as NavItem, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];

  /* ------------------------------------------------------------------
   * Hover: a "light rail". A hairline runs down the menu's left edge and a
   * short beam of indigo→purple light rides it, tracking the cursor's exact
   * height on a soft spring. Beside it, a faint wash of light follows the
   * pointer across the menu, and the option under it resolves into the
   * brand gradient. Nothing boxes or jumps — the light just finds you.
   * ------------------------------------------------------------------ */
  const asideRef = useRef<HTMLElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [inside, setInside] = useState(false);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);
  const wash = useMotionTemplate`radial-gradient(160px 70px at ${sx}px ${sy}px, rgba(168,85,247,0.09), rgba(67,56,202,0.04) 50%, rgba(67,56,202,0) 80%)`;

  /**
   * Pointer position in the aside's own CSS pixels. The app can be shown
   * zoomed or scaled (preview frames, browser zoom), where screen pixels and
   * layout pixels differ — so divide by the rendered/layout ratio, otherwise
   * the light lands above or below the option you're actually on.
   */
  const local = (clientX: number, clientY: number) => {
    const el = asideRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const scaleX = r.width / (el.offsetWidth || r.width) || 1;
    const scaleY = r.height / (el.offsetHeight || r.height) || 1;
    return {
      x: (clientX - r.left) / scaleX,
      y: (clientY - r.top) / scaleY
    };
  };

  // The beam settles on the centre of the option you're on; the wash
  // follows the cursor freely.
  const beamTarget = useMotionValue(0);
  const beamSpring = useSpring(beamTarget, SPRING);
  const beamY = useTransform(beamSpring, v => v - 28);
  const onMove = (e: React.PointerEvent<HTMLElement>) => {
    const p = local(e.clientX, e.clientY);
    if (!p) return;
    px.set(p.x);
    py.set(p.y);
  };
  const onEnterMenu = (e: React.PointerEvent<HTMLElement>) => {
    const p = local(e.clientX, e.clientY);
    if (!p) return;
    // Arrive where the cursor arrives — never slide in from the top.
    px.jump(p.x);
    py.jump(p.y);
    sx.jump(p.x);
    sy.jump(p.y);
    if (!inside) {
      beamTarget.jump(p.y);
      beamSpring.jump(p.y);
    }
    setInside(true);
  };
  const lit = (key: string) => hovered === key && !reduce;
  const itemHover = (key: string) => ({
    onPointerEnter: (e: React.PointerEvent<HTMLElement>) => {
      setHovered(key);
      const r = e.currentTarget.getBoundingClientRect();
      const p = local(r.left, r.top + r.height / 2);
      if (p) beamTarget.set(p.y);
    }
  });

  /** Label that resolves from slate into the brand gradient on hover. */
  const label = (id: string, text: string) => <span className="relative flex-1" data-id="element-4770" data-mp="333kkzaousnrb">
      <span className={`transition-opacity duration-200 ease-out ${lit(id) ? 'opacity-0' : 'opacity-100'}`} data-id="element-4771" data-mp="3h2tp3bzsbrex">
        {text}
      </span>
      <span aria-hidden className={`absolute inset-0 bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent transition-opacity duration-200 ease-out ${lit(id) ? 'opacity-100' : 'opacity-0'}`} data-id="element-4772" data-mp="3c51vmdjhrx8m">
        {text}
      </span>
    </span>;
  const iconCls = (id: string, on: boolean) => `w-5 h-5 transition-[transform,color] duration-200 ease-out ${on || lit(id) ? 'text-primary-indigo' : ''} ${lit(id) ? 'translate-x-1' : ''}`;
  return <aside ref={asideRef} onPointerMove={onMove} onPointerLeave={() => {
    setInside(false);
    setHovered(null);
  }} className="app-fill-screen hidden lg:flex lg:flex-col w-64 bg-white border-r border-slate-100 h-screen sticky top-0" data-id="element-4773" data-mp="3cxzuxxlwk2vy">
      {/* Ambient wash that follows the pointer. */}
      {!reduce && <motion.div aria-hidden className="pointer-events-none absolute inset-0" style={{
      backgroundImage: wash
    }} animate={{
      opacity: inside ? 1 : 0
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} data-id="element-4774" data-mp="2u06ssxj3rwzm" />}

      {/* The light rail: hairline track + a beam that rides it. */}
      <div aria-hidden className="pointer-events-none absolute left-2 top-[88px] bottom-4 w-px bg-slate-100 overflow-hidden" data-id="element-4775" data-mp="zra11uob6goq">
        {!reduce && <motion.div className="absolute left-0 top-[-88px] w-px h-14 bg-gradient-to-b from-transparent via-primary-indigo to-transparent" style={{
        y: beamY
      }} animate={{
        opacity: inside ? 1 : 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-4776" data-mp="qrwqszsd1o2y">
            <span className="absolute inset-x-0 top-1/4 bottom-1/4 bg-gradient-to-b from-primary-indigo via-ai-purple to-primary-indigo" data-id="element-4777" data-mp="1egpniygt3zym" />
          </motion.div>}
      </div>

      <div className="relative p-6" data-id="element-4778" data-mp="322au9cqvjeh">
        <Logo size="md" data-id="element-4779" data-mp="333n2h5hybxs" />
      </div>

      <nav onPointerEnter={onEnterMenu} className="relative flex-1 px-4 space-y-1 overflow-y-auto" data-id="element-4780" data-mp="2zzel7l00u1b">
        {navItems.map(item => {
        const locked = !isAuthenticated && item.to !== '/';
        if (locked) {
          return <button key={item.to} {...itemHover(item.to)} onClick={() => requireAuth(() => navigate(item.to))} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400" data-id="element-4781" data-mp="2eq79m412xquv">
                <item.icon className={`w-5 h-5 transition-transform duration-200 ease-out ${lit(item.to) ? 'translate-x-1' : ''}`} data-id="element-4782" data-mp="2siwycg7zt9vo" />
                <span className="flex-1 text-left" data-id="element-4783" data-mp="32gbaw4qspeip">{item.label}</span>
                <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors duration-200 ease-out" data-id="element-4784" data-mp="36h3kiz3kxlfi" />
              </button>;
        }
        return <NavLink key={item.to} {...itemHover(item.to)} to={item.to} onClick={item.to === '/' ? e => {
          // Clicking Discover always starts a fresh chat,
          // even when already on the Discover route.
          e.preventDefault();
          navigate('/', {
            state: {
              reset: Date.now()
            }
          });
        } : undefined} className={({
          isActive
        }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ease-out ${item.badge ? 'bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 text-primary-indigo font-medium ring-1 ring-primary-indigo/30 animate-pulse' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600'}`} data-id="element-4785" data-mp="1c20yjbdqolhc">
              {({
            isActive
          }) => <>
                  <item.icon className={iconCls(item.to, isActive || !!item.badge)} data-id="element-4786" data-mp="1y5gum9zduv3a" />
                  {label(item.to, item.label)}
                  {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-primary-indigo to-ai-purple text-white px-2 py-0.5 rounded-full shadow-sm shadow-primary-indigo/40" data-id="element-4787" data-mp="1o7kk9ke9s468">
                      New
                    </span>}
                </>}
            </NavLink>;
      })}
      </nav>

      {/* Help — pinned to the bottom */}
      <div onPointerEnter={onEnterMenu} className="relative px-4 pb-4 pt-2 border-t border-slate-100 mt-2" data-id="element-4788" data-mp="276ou2vdmy1yy">
        {!isAuthenticated ? <button {...itemHover('/help')} onClick={openLogin} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400" data-id="element-4789" data-mp="2c2ney8zr91um">
            <LifeBuoyIcon className={`w-5 h-5 transition-transform duration-200 ease-out ${lit('/help') ? 'translate-x-1' : ''}`} data-id="element-4790" data-mp="3r8p5l69u9294" />
            <span className="flex-1 text-left" data-id="element-4791" data-mp="3i6y2882rt7if">Help</span>
            <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors duration-200 ease-out" data-id="element-4792" data-mp="3i6fwmku7xgxu" />
          </button> : <NavLink {...itemHover('/help')} to="/help" className={({
        isActive
      }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ease-out ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600'}`} data-id="element-4793" data-mp="38heeop444ph8">
            {({
          isActive
        }) => <>
                <LifeBuoyIcon className={iconCls('/help', isActive)} data-id="element-4794" data-mp="2pik4ltflgd7e" />
                {label('/help', 'Help')}
              </>}
          </NavLink>}
      </div>
    </aside>;
}